import { sprintf } from 'sprintf-js';

import type { ApiAreaOfFocus } from 'api/types/ApiAreaOfFocus';
import type {
  ApiISODateString,
  ApiISODateTimeNoTzString,
} from 'api/types/ApiTypedDate';
import {
  FormattedDate,
  FormattedDateTime,
  SingleDayDateTime,
} from 'components/Date/DateUtil';
import { DetailsList } from 'components/DetailsList/DetailsList';
import { DetailsListItem } from 'components/DetailsList/Item/DetailsListItem';
import { AREAS_OF_FOCUS } from 'utils/constants/general/areasOfFocus';
import { EXPECTED_TIMES } from 'utils/constants/general/expectedTimes';
import { EXPECTED_TIMES_ONCE } from 'utils/constants/general/expectedTimesOnce';
import { EXPECTED_TIMES_RECURRING } from 'utils/constants/general/expectedTimesRecurring';
import type { TimeOfDay } from 'utils/constants/general/timesOfDay';

const formatAmountString = (amount: string) => {
  const parts = amount.split('.');
  const cents = parseInt(parts[1], 10);
  const [dollars] = parts;
  return `$${dollars}${cents > 0 ? `.${cents}` : ''}`;
};

const formatTimesOfDay = (timesOfDay: TimeOfDay[]) => {
  const weekdays = [];
  const weekends = [];

  if (timesOfDay.includes('WEEKDAYS_DAYTIME'))
    weekdays.push(getText('daytime'));
  if (timesOfDay.includes('WEEKDAYS_EVENINGS'))
    weekdays.push(getText('evenings'));
  if (timesOfDay.includes('WEEKENDS_DAYTIME'))
    weekends.push(getText('daytime'));
  if (timesOfDay.includes('WEEKENDS_EVENINGS'))
    weekends.push(getText('evenings'));

  // Weekends (daytime, evenings), Weekdays (daytime, evenings)
  return [
    weekdays.length > 0 &&
      sprintf(`%(weekPart)s (%(dayPart)s)`, {
        weekPart: getText('Weekdays'),
        dayPart: weekdays.join(', '),
      }),
    weekends.length > 0 &&
      sprintf(`%(weekPart)s (%(dayPart)s)`, {
        weekPart: getText('Weekends'),
        dayPart: weekends.join(', '),
      }),
  ]
    .filter(Boolean)
    .join(', ');
};

type Props = {
  areasOfFocus: ApiAreaOfFocus[];
  startDate: ApiISODateString | null;
  endDate: ApiISODateString | null;
  startsLocal: ApiISODateTimeNoTzString | null;
  endsLocal: ApiISODateTimeNoTzString | null;
  feeAmount: string | null;
  feeRequired: boolean;
  driversLicenseRequired: boolean;
  backgroundCheckRequired: boolean;
  orientationRequired: boolean;
  isRecurring: boolean;
  timezone: string | null;
  welcomeFamilies: boolean;
  welcomeTeens: boolean;
  welcomeGroups: boolean;
  welcomeAge55Plus: boolean;
  welcomeIntl: boolean;
  welcomePrivateCorpGroups: boolean;
  detailsStipendProvided: boolean;
  detailsTrainingProvided: boolean;
  detailsHousingAvailable: boolean;
  detailsCulturalSupport: boolean;
  detailsAcademicCreditAvailable: boolean;
  detailsWheelchairAccessible: boolean;
  timesOfDay: TimeOfDay[];
  expectedTime: string | null;
  ageRequirement: number | null;
  otherRequirements: string | null;
  numVolunteersNeeded: number | null;
  commitmentDetails: string | null;
};

const REQUIREMENTS_LABELS: Record<string, string> = {
  driversLicenseRequired: getText('Driver’s License'),
  backgroundCheckRequired: getText('Background Check'),
  orientationRequired: getText('Attend Orientation'),
};

const WELCOME_LABELS: Record<string, string> = {
  welcomeFamilies: getText('Kids'),
  welcomeTeens: getText('Teens'),
  welcomeGroups: getText('Public Groups'),
  welcomeAge55Plus: getText('Age 55+'),
  welcomeIntl: getText('International Volunteers'),
  welcomePrivateCorpGroups: getText('Private Corporate Groups'),
};

const BENEFIT_LABELS: Record<string, string> = {
  detailsStipendProvided: getText('Stipend Provided'),
  detailsTrainingProvided: getText('Training Provided'),
  detailsHousingAvailable: getText('Housing Available'),
  detailsCulturalSupport: getText('Language / Cultural Support Available'),
  detailsAcademicCreditAvailable: getText('Academic Credit Available'),
};

export function VolopDetailsList({
  areasOfFocus,
  startDate,
  endDate,
  startsLocal,
  endsLocal,
  driversLicenseRequired,
  backgroundCheckRequired,
  orientationRequired,
  feeRequired,
  feeAmount,
  isRecurring,
  timezone,
  welcomeFamilies,
  welcomeTeens,
  welcomeGroups,
  welcomeAge55Plus,
  welcomeIntl,
  welcomePrivateCorpGroups,
  detailsStipendProvided,
  detailsTrainingProvided,
  detailsHousingAvailable,
  detailsCulturalSupport,
  detailsAcademicCreditAvailable,
  detailsWheelchairAccessible,
  timesOfDay,
  expectedTime,
  ageRequirement,
  otherRequirements,
  numVolunteersNeeded,
  commitmentDetails,
}: Props) {
  const formattedExpectedTime =
    expectedTime === 'NONE'
      ? null
      : [
          ...EXPECTED_TIMES,
          ...EXPECTED_TIMES_ONCE,
          ...EXPECTED_TIMES_RECURRING,
        ].find(({ id }) => id === expectedTime);

  const allRequirements = [
    driversLicenseRequired && 'driversLicenseRequired',
    backgroundCheckRequired && 'backgroundCheckRequired',
    orientationRequired && 'orientationRequired',
  ].filter(Boolean);

  const allWelcome = [
    welcomeFamilies && 'welcomeFamilies',
    welcomeTeens && 'welcomeTeens',
    welcomeGroups && 'welcomeGroups',
    welcomeAge55Plus && 'welcomeAge55Plus',
    welcomeIntl && 'welcomeIntl',
    welcomePrivateCorpGroups && 'welcomePrivateCorpGroups',
  ].filter(Boolean);

  const allBenefits = [
    detailsStipendProvided && 'detailsStipendProvided',
    detailsTrainingProvided && 'detailsTrainingProvided',
    detailsHousingAvailable && 'detailsHousingAvailable',
    detailsCulturalSupport && 'detailsCulturalSupport',
    detailsAcademicCreditAvailable && 'detailsAcademicCreditAvailable',
  ].filter(Boolean);

  return (
    <DetailsList>
      {startDate && endDate && startDate === endDate && (
        <DetailsListItem
          title={getText('Date')}
          iconName="calendar"
          value={
            <SingleDayDateTime
              startDate={startsLocal ?? startDate}
              endDate={endsLocal ?? endDate}
              showStartTime={Boolean(startsLocal)}
              showEndTime={Boolean(endsLocal)}
              timezone={timezone}
            />
          }
        />
      )}

      {startDate && startDate !== endDate && (
        <DetailsListItem
          title={getText('Start Date')}
          iconName="hourglass"
          value={
            startsLocal ? (
              <FormattedDateTime dateTime={startsLocal} timezone={timezone} />
            ) : (
              <FormattedDate date={startDate} />
            )
          }
        />
      )}

      {endDate && endDate !== startDate && (
        <DetailsListItem
          title={getText('End Date')}
          iconName="hourglass"
          value={
            endsLocal ? (
              <FormattedDateTime dateTime={endsLocal} timezone={timezone} />
            ) : (
              <FormattedDate date={endDate} />
            )
          }
        />
      )}

      {timesOfDay.length > 0 && (
        <DetailsListItem
          title={getText('Available Times')}
          iconName="calendar"
          value={formatTimesOfDay(timesOfDay)}
        />
      )}

      {formattedExpectedTime && (
        <DetailsListItem
          title={getText('Time Commitment')}
          iconName="clock"
          value={formattedExpectedTime.name}
        />
      )}

      {commitmentDetails && (
        <DetailsListItem
          title={getText('Commitment Details')}
          iconName="clock"
          value={commitmentDetails}
        />
      )}

      <DetailsListItem
        title={getText('Recurrence')}
        iconName="hourglass"
        value={isRecurring ? getText('Recurring') : getText('One time only')}
      />

      {numVolunteersNeeded && (
        <DetailsListItem
          title={getText('Volunteers Needed')}
          iconName="two-people"
          value={numVolunteersNeeded}
        />
      )}

      {feeRequired && (
        <DetailsListItem
          title={getText('Cost')}
          iconName="coin"
          value={`${getText(
            'Fee Required',
          )}${feeAmount ? `: ${formatAmountString(feeAmount)}` : ''}`}
        />
      )}

      {areasOfFocus?.length !== 0 && (
        <DetailsListItem
          iconName="globe"
          title={getText('Cause Areas')}
          value={areasOfFocus.map((item) => AREAS_OF_FOCUS[item]).join(', ')}
        />
      )}

      {allBenefits.length > 0 && (
        <DetailsListItem
          iconName="percentage-award"
          title={getText('Benefits')}
          value={allBenefits.map((k) => BENEFIT_LABELS[k]).join(', ')}
        />
      )}

      {allWelcome.length > 0 && (
        <DetailsListItem
          iconName="group"
          title={getText('Good For')}
          value={allWelcome.map((item) => WELCOME_LABELS[item]).join(', ')}
        />
      )}

      {allRequirements.length > 0 && (
        <DetailsListItem
          iconName="paper"
          title={getText('Participation Requirements')}
          value={allRequirements.map((k) => REQUIREMENTS_LABELS[k]).join(', ')}
        />
      )}

      {ageRequirement && (
        <DetailsListItem
          iconName="cms/cms-persona-quiz"
          title={getText('Age Requirement')}
          value={`${ageRequirement}+`}
        />
      )}

      {otherRequirements && (
        <DetailsListItem
          iconName="clipboard"
          title={getText('Other Requirements')}
          value={otherRequirements}
        />
      )}

      {detailsWheelchairAccessible && (
        <DetailsListItem
          iconName="wheelchair"
          value={getText('Wheelchair Accessible')}
        />
      )}
    </DetailsList>
  );
}
